// components/entities/SlideCorrectionRenderer.tsx
import React, { memo, useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import { useKernel } from '@/kernel/Kernel';
import { useKernelStore } from '@/kernel/kernelStores';
import { Check, ExternalLink, X } from '@/components/ui/lucide-memo';
import { PageCrop } from '../portal/PageCrop';
import { buildSlidesDeepLink } from '#lib/leasing/portal-url-state';
import type { BaseEntityProps, SlideCorrectionDTO, SlideCorrectionShape } from '#types/types';
import { buildNoteLayout } from '#lib/slides/noteLayout';
import { NOTE_SEVERITY_COLORS } from '../slides/slidesUi';
import { EntitySkeleton } from './EntityView';
import { formatRelativeTimestamp, resolveCellStyle } from './shortlistCells';
import { useSlidesStore } from '../slides/slidesStore';
import type { TableColumnDef } from '../portal/Table';
import styles from '../portal/Table.module.css';

const SUGGESTED: Record<NonNullable<SlideCorrectionDTO['suggested']>, { label: string; hex: string }> = {
  correction: { label: 'Correction', hex: '#4DAB9A' },
  preference: { label: 'Preference', hex: '#979A9B' },
  'one-off': { label: 'One-off', hex: '#FFA344' },
  noise: { label: 'Noise', hex: '#979A9B' },
  contradiction: { label: 'Contradiction', hex: '#FF7369' },
};
const DECISION: Record<Exclude<SlideCorrectionDTO['decision'], 'pending'>, { label: string; hex: string }> = {
  approved: { label: 'Approved', hex: '#4DAB9A' },
  rejected: { label: 'Rejected', hex: '#979A9B' },
  'held-out': { label: 'Held out', hex: '#9A6DD7' },
};
/** The decision as a word; one the pre-judge made says so, so the expert knows nobody checked it. */
const decisionPill = (row: Row) => {
  const { label, hex } = DECISION[row.decision as keyof typeof DECISION];
  return { label: row.auto && row.decision !== 'held-out' ? `Auto-${label.toLowerCase()}` : label, hex };
};
const CHANGE: Record<SlideCorrectionDTO['change'], string> = { judgement: 'Judgement', wording: 'Wording', layout: 'Layout', added: 'Added', removed: 'Removed' };

// A status is a word in grey, ChatGPT's way (owner, 2026-09-29: 'no weird colours'), not a
// coloured pill. `hex` stays in the tables for anything that still needs the category's colour.
const Pill = ({ label }: { label: string; hex: string }) =>
  <span className="shrink-0 text-[#AFAFAF]" style={{ fontSize: 13 }}>{label}</span>;


type Row = SlideCorrectionDTO;
const NOTE_FONT = 'Helvetica, Arial, sans-serif';
const sameFrame = (a?: SlideCorrectionShape | null, b?: SlideCorrectionShape | null) =>
  !!a && !!b && ['x', 'y', 'w', 'h'].every(k => Math.round(a.frame[k as 'x']) === Math.round(b.frame[k as 'x']));
const sameAnchor = (a?: SlideCorrectionShape | null, b?: SlideCorrectionShape | null) =>
  Math.round(a?.anchor?.x ?? -1) === Math.round(b?.anchor?.x ?? -1) && Math.round(a?.anchor?.y ?? -1) === Math.round(b?.anchor?.y ?? -1);

/** What the person did, in one sentence a reviewer reads without the data model. */
export function correctionSentence(row: Row): string {
  const who = row.byName || 'Someone';
  const what = row.shapes?.after?.type === 'note' || row.shapes?.before?.type === 'note' || !row.shapes ? 'note' : 'mark';
  if (row.change === 'added') return `${who} added a ${what}.`;
  if (row.change === 'removed') return `${who} removed the Reviewer's ${what}.`;
  const on = row.shapes ? `the Reviewer's ${what}` : `the Reviewer's comment on ${row.point}`;
  if (row.change === 'wording') return !row.before ? `${who} wrote the text of this ${what}.` : !row.after ? `${who} cleared ${on}.` : `${who} reworded ${on}.`;
  if (row.change === 'judgement') return row.before && row.after
    ? `${who} changed the verdict on ${row.point} from ${row.before} to ${row.after}.` : `${who} changed the verdict on ${row.point}.`;
  const { before, after } = row.shapes ?? {};
  const differs = (keys: Array<'x' | 'y' | 'w' | 'h'>) => !!before && !!after && keys.some(key => Math.round(before.frame[key]) !== Math.round(after.frame[key]));
  const verbs = [differs(['x', 'y']) && 'moved', differs(['w', 'h']) && 'resized'].filter(Boolean).join(' and ');
  const pointed = !sameAnchor(before, after);
  const said = verbs ? `${verbs} the ${what}${pointed ? ' and changed where it points' : ''}` : pointed ? `changed where the ${what} points` : `moved the ${what}`;
  return `${who} ${said}.`;
}

/** The Reviewer's text struck through, the person's below it; nothing for a move. */
export function CorrectionTexts({ row, clamp }: { row: Row; clamp?: boolean }) {
  if (row.change === 'layout' || row.change === 'judgement') return null;
  const lines = clamp ? 'line-clamp-3' : '';
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {row.before && row.change !== 'added'
        ? <p className={clsx(lines, 'whitespace-pre-line text-[#8E8E8E]', row.change !== 'removed' && 'line-through decoration-[#8E8E8E]/60')} style={{ fontSize: 14, lineHeight: '21px' }} title={row.before}>{row.before}</p>
        : null}
      {row.after && row.change !== 'removed'
        ? <p className={clsx(lines, 'whitespace-pre-line text-[#ECECEC]')} style={{ fontSize: 14, lineHeight: '21px' }} title={row.after}>{row.after}</p>
        : null}
    </div>
  );
}

function ShapeOnPage({ shape, page, ghost }: { shape: SlideCorrectionShape; page: { w: number; h: number }; ghost?: boolean }) {
  const color = ghost ? '#8A8A8A' : shape.color ?? NOTE_SEVERITY_COLORS[shape.severity ?? 'info'];
  const k = page.w / 842;
  const dash = ghost ? `${5 * k} ${4 * k}` : undefined;
  if (shape.type !== 'note') {
    const f = shape.frame;
    return <rect x={Math.min(f.x, f.x + f.w)} y={Math.min(f.y, f.y + f.h)} width={Math.abs(f.w)} height={Math.abs(f.h)}
      fill="none" stroke={color} strokeWidth={2 * k} strokeDasharray={dash} />;
  }
  const l = buildNoteLayout({ frame: shape.frame, text: shape.text ?? '', rotation: shape.rotation, anchor: shape.anchor }, page.w, page.h);
  const stroke = l.strokeWidth * (ghost ? 1.6 : 1.4);
  return (
    <g opacity={ghost ? 0.85 : 1}>
      {l.leader && <line x1={l.leader.start.x} y1={l.leader.start.y} x2={l.leader.end.x} y2={l.leader.end.y} stroke={color} strokeWidth={stroke} strokeDasharray={dash} />}
      {l.anchor && <circle cx={l.anchor.x} cy={l.anchor.y} r={l.anchorRadius * 1.3} fill={color} />}
      <g transform={`translate(${l.frame.x} ${l.frame.y}) rotate(${l.rotation} ${l.frame.w / 2} ${l.frame.h / 2})`}>
        <path d={l.path} fill="#fff" fillOpacity={ghost ? 0.6 : 0.97} stroke={color} strokeWidth={stroke} strokeDasharray={dash} />
        {l.lines.map((line, i) => <text key={i} x={l.padding} y={l.padding + l.fontSize + i * l.lineHeight} fill={color}
          fontFamily={NOTE_FONT} fontSize={l.fontSize} style={{ whiteSpace: 'pre' }}>{line}</text>)}
      </g>
    </g>
  );
}

/** The page zoomed on what changed, both versions drawn where they sit; the whole page in the detail. */
export function CorrectionFigure({ row, whole, className }: { row: Row; whole?: boolean; className?: string }) {
  const ctx = row.context;
  if (!ctx || !row.shapes) return (
    <div className={clsx('flex aspect-[16/9] w-full flex-col items-center justify-center gap-1 bg-[#141414] px-6 text-center', className)}>
      <span className="text-[#8E8E8E]" style={{ fontSize: 12 }}>Validation stamp</span>
      <span className="text-[#ECECEC]" style={{ fontSize: 15, lineHeight: '20px' }}>{row.point}</span>
    </div>
  );
  const page = { w: ctx.pageWidth, h: ctx.pageHeight };
  const { before, after } = row.shapes ?? {};
  // The Reviewer's version is drawn only where it sat elsewhere; a reworded note is one shape, its texts below.
  const showBefore = !!before && (!after || !sameFrame(before, after) || !sameAnchor(before, after));
  const ratio = 16 / 9;
  let box = { x: 0, y: 0, w: page.w, h: page.h };
  if (!whole) {
    const pts: Array<{ x: number; y: number }> = [{ x: ctx.x, y: ctx.y }];
    for (const s of [before, after]) if (s) {
      pts.push({ x: s.frame.x, y: s.frame.y }, { x: s.frame.x + s.frame.w, y: s.frame.y + s.frame.h });
      if (s.anchor) pts.push(s.anchor);
    }
    const xs = pts.map(p => p.x), ys = pts.map(p => p.y), pad = page.w * 0.05;
    let w = Math.max(Math.max(...xs) - Math.min(...xs) + 2 * pad, page.w * 0.4);
    let h = Math.max(Math.max(...ys) - Math.min(...ys) + 2 * pad, w / ratio);
    w = Math.max(w, h * ratio); h = w / ratio;
    const cx = (Math.max(...xs) + Math.min(...xs)) / 2, cy = (Math.max(...ys) + Math.min(...ys)) / 2;
    const fit = (c: number, size: number, max: number) => size >= max ? (max - size) / 2 : Math.min(Math.max(c - size / 2, 0), max - size);
    box = { x: fit(cx, w, page.w), y: fit(cy, h, page.h), w, h };
  }
  return (
    <div className={clsx('relative w-full bg-white', whole ? '' : 'aspect-[16/9]', className)} style={whole ? { aspectRatio: `${page.w} / ${page.h}` } : undefined}>
      <svg viewBox={`${box.x} ${box.y} ${box.w} ${box.h}`} preserveAspectRatio="xMidYMid meet" className="absolute inset-0 h-full w-full" role="img"
        aria-label={`Page ${row.page}: ${correctionSentence(row)}`}>
        {ctx.snapshotUrl ? <image href={ctx.snapshotUrl} x={0} y={0} width={page.w} height={page.h} preserveAspectRatio="none" /> : null}
        {showBefore && before ? <ShapeOnPage shape={before} page={page} ghost={!!after} /> : null}
        {after ? <ShapeOnPage shape={after} page={page} /> : null}
        {!before && !after ? <circle cx={ctx.x} cy={ctx.y} r={page.w * 0.012} fill="none" stroke="#3B82F6" strokeWidth={page.w * 0.003} /> : null}
      </svg>
      {showBefore && before && after ? (
        <div className="pointer-events-none absolute bottom-2 left-2 flex items-center gap-3 rounded-full bg-[#262626]/90 px-2.5 py-1 text-[#ECECEC]" style={{ fontSize: 12 }}>
          <span className="inline-flex items-center gap-1.5" style={{ fontSize: 'inherit' }}><span className="inline-block h-0 w-4 border-t-2 border-dashed border-[#AFAFAF]" />The Reviewer</span>
          <span className="inline-flex items-center gap-1.5" style={{ fontSize: 'inherit' }}><span className="inline-block h-0 w-4 border-t-2 border-[#ECECEC]" />{row.byName || 'Edited'}</span>
        </div>
      ) : null}
    </div>
  );
}

export function CorrectionActions({ row, decided, decide }: { row: Row; decided: boolean; decide: (e: React.MouseEvent, d: 'approved' | 'rejected' | 'pending') => void }) {
  if (!decided && row.canDecide) return <>
    <button type="button" onClick={e => decide(e, 'rejected')} aria-label="Reject"
      className="h-8 rounded-full px-3 text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white" style={{ fontSize: 13 }}>Reject</button>
    <button type="button" onClick={e => decide(e, 'approved')} aria-label="Approve as example"
      className="inline-flex h-8 items-center gap-1.5 rounded-full bg-[#F0F0F0] px-3 text-black transition-colors hover:bg-white" style={{ fontSize: 13 }}>
      <Check size={15} strokeWidth={2} />Approve
    </button>
  </>;
  if (!decided) return null;
  return <>
    <span className="px-1 text-[#ECECEC]" style={{ fontSize: 13 }}>{decisionPill(row).label}</span>
    {row.canDecide ? <button type="button" onClick={e => decide(e, 'pending')} aria-label="Undo this decision"
      className="h-8 rounded-full px-3 text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white" style={{ fontSize: 13 }}>Undo</button> : null}
  </>;
}

/** The full correction: the whole page with both versions, every word of both texts, the
 *  pre-check's reason, and the decision. Escape or a click outside closes it. */
function CorrectionDetail({ row, onClose, decide, openInDeck }: { row: Row; onClose: () => void;
  decide: (e: React.MouseEvent, d: 'approved' | 'rejected' | 'pending') => void; openInDeck: (e: React.MouseEvent) => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const caption = (label: string) => <div className="mb-1 text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>{label}</div>;
  return createPortal(
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 p-6" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={`${row.tenant}, page ${row.page}`} onClick={e => e.stopPropagation()}
        className="flex max-h-full w-full max-w-[1280px] overflow-hidden rounded-[24px] bg-[#212121]">
        <div className="flex min-w-0 flex-[3] items-center justify-center bg-[#141414] p-4">
          <CorrectionFigure row={row} whole className="max-h-[calc(100vh-80px)] rounded-[12px] overflow-hidden" />
        </div>
        <div className="flex w-[380px] shrink-0 flex-col gap-4 overflow-y-auto p-6">
          <header className="flex min-w-0 items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-medium text-[#ECECEC]" style={{ fontSize: 16, lineHeight: '22px' }}>{[row.tenant, row.centre].filter(Boolean).join(' · ')}</div>
              <div className="text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>
                {[row.point, `page ${row.page}`, formatRelativeTimestamp(row.createdAt, 'en')].filter(Boolean).join(' · ')}
              </div>
            </div>
            <button type="button" aria-label="Close" onClick={onClose}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white">
              <X size={18} strokeWidth={1.8} />
            </button>
          </header>
          <p className="text-[#ECECEC]" style={{ fontSize: 15, lineHeight: '22px' }}>{correctionSentence(row)}</p>
          {row.change !== 'layout' && row.before && row.change !== 'added'
            ? <div>{caption(row.change === 'removed' ? 'What was removed' : 'The Reviewer wrote')}<p className="whitespace-pre-line text-[#AFAFAF]" style={{ fontSize: 14, lineHeight: '21px' }}>{row.before}</p></div> : null}
          {row.change !== 'layout' && row.after && row.change !== 'removed'
            ? <div>{caption(`${row.byName || 'The edit'}${row.change === 'added' ? ' wrote' : '’s version'}`)}<p className="whitespace-pre-line text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '21px' }}>{row.after}</p></div> : null}
          {row.suggested
            ? <div>{caption('Pre-check')}<p className="text-[#AFAFAF]" style={{ fontSize: 14, lineHeight: '21px' }}>
              {`Looks like ${SUGGESTED[row.suggested].label.toLowerCase()}.`}{row.suggestedReason ? ` ${row.suggestedReason}` : ''}</p></div> : null}
          <footer className="mt-auto flex flex-wrap items-center gap-1 pt-2">
            <button type="button" onClick={openInDeck}
              className="inline-flex h-8 items-center gap-1.5 rounded-full border border-[#2A2A2A] px-3 text-[#ECECEC] transition-colors hover:bg-white/[0.06]" style={{ fontSize: 13 }}>
              <ExternalLink size={14} strokeWidth={1.8} />Open in deck
            </button>
            <div className="ml-auto flex items-center gap-1">
              <CorrectionActions row={row} decided={row.decision !== 'pending'} decide={decide} />
            </div>
          </footer>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export interface SlideCorrectionRendererProps extends BaseEntityProps<SlideCorrectionDTO> {
  columns?: TableColumnDef[];
}

export const SlideCorrectionRenderer = memo(({ entityId, variant = 'row', onClick, className, columns }: SlideCorrectionRendererProps) => {
  const { kernel } = useKernel();
  const row = useKernelStore((state) => state.inventory.SlideCorrection.registry[entityId]);
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  // The ledger decides what was stored (an approval may land held out); the row follows it.
  const decide = useCallback(async (event: React.MouseEvent, decision: 'approved' | 'rejected' | 'pending') => {
    event.stopPropagation();
    const current = kernel.current?.getState().inventory.SlideCorrection.registry[entityId];
    if (!current) return;
    try {
      const stored = await useSlidesStore.getState().decideTraining({ projectId: current.projectId, runId: current.runId, changeId: current.changeId, decision });
      kernel.current?.applyInventoryEvents([{ entityType: 'SlideCorrection', entityId, eventType: 'UPDATE', payload: { decision: stored, auto: false } }]);
      kernel.current?.logUserAction('Slides training decision', { projectId: current.projectId, decision: stored });
    } catch (error) {
      kernel.current?.handleError(error instanceof Error ? error.message : String(error));
    }
  }, [entityId, kernel]);

  if (!row) return <EntitySkeleton variant={variant} className={className} />;

  const openInDeck = (e: React.MouseEvent) => {
    e.stopPropagation();
    kernel.current?.logUserAction('Slides training row opened in deck', { projectId: row.projectId, pageId: row.pageId, elementId: row.elementId });
    window.open(buildSlidesDeepLink({ projectId: row.projectId, pageId: row.pageId, elementId: row.elementId }), '_blank', 'noopener');
  };

  if (variant === 'card') {
    // A queue of decisions (owner, 2026-10-13: "we just see what Alice did, not the note, not the
    // page"): the page itself, zoomed on the note, the Reviewer's version dashed and the person's in
    // its colour, then one sentence and the two texts. The card opens the full detail.
    const decided = row.decision !== 'pending';
    return (
      <article role="button" tabIndex={0} onClick={() => setOpen(true)} onKeyDown={e => { if (e.key === 'Enter') setOpen(true); }}
        aria-label={`${row.tenant}: ${correctionSentence(row)}`}
        className={clsx('flex min-w-0 cursor-pointer flex-col overflow-hidden rounded-[16px] bg-[#1B1B1B] transition-colors hover:bg-[#212121] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#444444]', className)}>
        <CorrectionFigure row={row} />
        {open ? <CorrectionDetail row={row} onClose={close} decide={(e, d) => void decide(e, d)} openInDeck={openInDeck} /> : null}
        <div className="flex min-w-0 flex-1 flex-col gap-3 p-4">
          <div className="min-w-0">
            {/* The dossier name focuses the queue on that review's edits. */}
            <button type="button" title={`Only ${row.dossier}`} onClick={e => { e.stopPropagation(); useSlidesStore.getState().patchTrainingExplorer({ projectId: row.projectId }); }}
              className="block max-w-full truncate text-left font-medium text-[#ECECEC] underline-offset-2 hover:underline" style={{ fontSize: 14, lineHeight: '20px' }}>
              {[row.tenant, row.centre].filter(Boolean).join(' · ')}
            </button>
            <div className="truncate text-[#8E8E8E]" style={{ fontSize: 13, lineHeight: '18px' }}>
              {[row.point, `page ${row.page}`, formatRelativeTimestamp(row.createdAt, 'en')].filter(Boolean).join(' · ')}
            </div>
          </div>
          <p className="text-[#ECECEC]" style={{ fontSize: 14, lineHeight: '20px' }}>{correctionSentence(row)}</p>
          <CorrectionTexts row={row} clamp />
          <footer className="mt-auto flex min-w-0 items-center gap-2 pt-1">
            {row.suggested ? <span className="min-w-0 truncate text-[#8E8E8E]" style={{ fontSize: 13 }} title={row.suggestedReason ?? undefined}>
              {`Looks like ${SUGGESTED[row.suggested].label.toLowerCase()}`}</span> : null}
            <div className="ml-auto flex shrink-0 items-center gap-1">
              <CorrectionActions row={row} decided={decided} decide={(e, d) => void decide(e, d)} />
            </div>
          </footer>
        </div>
      </article>
    );
  }
  if (variant !== 'row') return null;

  const cells: Record<string, React.ReactNode> = {
    dossier: (
      <div className="flex min-w-0 items-center gap-2.5">
        {row.context?.snapshotUrl ? <PageCrop {...row.context} src={row.context.snapshotUrl} width={72} height={48} alt="" /> : null}
        <div className="flex min-w-0 flex-col justify-center gap-0.5">
          <span className="truncate text-[13px] text-[#E8E8E8]" title={row.dossier}>{row.tenant}</span>
          <span className="truncate text-xs text-[#6E6E6E]">{[row.centre, formatRelativeTimestamp(row.createdAt, 'en')].filter(Boolean).join(' · ')}</span>
        </div>
        <button type="button" title="Open in deck" aria-label="Open in deck"
          onClick={openInDeck}
          className="ml-auto inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#6E6E6E] transition-colors hover:bg-white/10 hover:text-[#E8E8E8]"><ExternalLink size={13} strokeWidth={1.8} /></button>
      </div>
    ),
    point: <span className="truncate text-xs text-[#B5B5B5]" title={row.point}>{row.point}</span>,
    page: <span className="font-mono text-xs text-[#B5B5B5]">{row.page}</span>,
    before: <span className="line-clamp-3 text-xs leading-snug text-[#6E6E6E]" title={row.before}>{row.before}</span>,
    after: <span className="line-clamp-3 text-xs leading-snug text-[#E8E8E8]" title={row.after}>{row.after}</span>,
    change: <span className="text-xs text-[#B5B5B5]">{CHANGE[row.change]}</span>,
    by: <span className="truncate text-xs text-[#B5B5B5]">{row.byName}</span>,
    // One pill on the row's own height; the reason is a hover, the way every other table keeps
    // its rows one line tall and its detail one gesture away.
    suggested: row.suggested
      ? <span title={row.suggestedReason ?? undefined} className="inline-flex"><Pill {...SUGGESTED[row.suggested]} /></span>
      : <span className="text-xs text-[#6E6E6E]">Not judged yet</span>,
    decision: row.decision === 'pending'
      ? row.canDecide
        ? (
          <div className="flex items-center gap-1.5">
            <button type="button" title="Approve as example" aria-label="Approve as example" onClick={e => void decide(e, 'approved')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#ECECEC] transition-colors hover:bg-white/[0.08]"><Check size={16} strokeWidth={2} /></button>
            <button type="button" title="Reject" aria-label="Reject" onClick={e => void decide(e, 'rejected')}
              className="inline-flex h-8 w-8 items-center justify-center rounded-full text-[#B4B4B4] transition-colors hover:bg-white/[0.08] hover:text-white"><X size={16} strokeWidth={2} /></button>
          </div>
        )
        : <span className="text-xs text-[#6E6E6E]">Waiting for the expert</span>
      : row.canDecide
        ? <button type="button" title="Undo this decision" aria-label="Undo this decision" onClick={e => void decide(e, 'pending')} className="inline-flex"><Pill {...decisionPill(row)} /></button>
        : <Pill {...decisionPill(row)} />,
  };

  return (
    <div onClick={onClick} className={clsx(styles.gridRow, styles.bodyRow, 'cursor-default', className)}>
      {(columns ?? []).map(column => (
        <div key={column.id} className={clsx(styles.cell, '!py-2')} style={resolveCellStyle(column)}>
          {cells[column.id] ?? null}
        </div>
      ))}
    </div>
  );
});
SlideCorrectionRenderer.displayName = 'SlideCorrectionRenderer';
